import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import QRCodeComponent from './QRcode';
import { useNavigate } from 'react-router-dom';
import { getLastIncrementDate, getPointTransactions, getRankBadge } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface userinfo {
  name: string;
  email: string;
  id: string;
  rank: number;
  points: number;
  times: number;
}

interface IDComponentProps {
  message?: userinfo;
  estimateRank: (points: number | undefined, times: number | undefined) => number;
  seacret_key: string;
}

interface PointTransaction {
  transaction_type: string;
  amount: number;
  created_at: string;
}

const IDComponent: React.FC<IDComponentProps> = ({ message, estimateRank, seacret_key }) => {
  const navigate = useNavigate();
  const { token, refreshTokenFunction } = useAuth();
  const [lastIncrementDate, setLastIncrementDate] = useState<string | null>(null);
  const [badgeUrl, setBadgeUrl] = useState<string | null>(null);
  const [badgeError, setBadgeError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false); // QRコードは即座に表示できるのでfalse

  const getLatestAwardDateFromTransactions = (transactions: PointTransaction[]): string | null => {
    // 履歴一覧と同じデータソースを使い、付与系のみを最終付与日として採用する
    const awardedTransactions = transactions.filter((tx) => {
      if (!tx?.created_at) return false;
      if (tx.transaction_type === 'purchase' || tx.transaction_type === 'visit') return true;
      if (tx.transaction_type === 'adjust') return tx.amount > 0;
      return false;
    });

    if (awardedTransactions.length === 0) return null;

    const latest = awardedTransactions.reduce((currentLatest, tx) => {
      return new Date(tx.created_at).getTime() > new Date(currentLatest.created_at).getTime()
        ? tx
        : currentLatest;
    });

    return latest.created_at;
  };

  useEffect(() => {
    const fetchData = async () => {
      // キャッシュから最終来店日とバッジURLを読み込む
      const cachedLastDate = localStorage.getItem('lastIncrementDate');
      const cachedBadgeUrl = localStorage.getItem('badgeUrl');
      
      if (cachedLastDate) {
        setLastIncrementDate(cachedLastDate);
      }
      if (cachedBadgeUrl) {
        setBadgeUrl(cachedBadgeUrl);
      }

      try {
        const currentToken = token || localStorage.getItem('token');
        if (currentToken) {
          // 最終付与日は履歴ベースで取得（usersettingのマイル履歴と同じ基準）
          try {
            const txResponse = await getPointTransactions(currentToken);
            const latestAwardDate = getLatestAwardDateFromTransactions(txResponse.data || []);

            if (latestAwardDate) {
              setLastIncrementDate(latestAwardDate);
              localStorage.setItem('lastIncrementDate', latestAwardDate);
            }
          } catch (dateError) {
            // フォールバック: 既存のlast_increment_dateを利用
            try {
              const dateResponse = await getLastIncrementDate(currentToken);
              if (dateResponse.data?.last_increment_date) {
                setLastIncrementDate(dateResponse.data.last_increment_date);
                localStorage.setItem('lastIncrementDate', dateResponse.data.last_increment_date);
              }
            } catch (fallbackError) {
              console.error('Failed to fetch last increment date, using cached:', fallbackError);
            }
          }

          // バッジ画像を取得
          try {
            const badgeResponse = await getRankBadge(currentToken);
            const badgeBlob = new Blob([badgeResponse.data], { type: badgeResponse.headers['content-type'] || 'image/png' });
            const url = URL.createObjectURL(badgeBlob);
            setBadgeUrl(url);
            setBadgeError(false);
            
            // Blobを直接保存できないので、必要に応じてBase64に変換して保存
            // ここではキャッシュヘッダーで対応しているのでスキップ
          } catch (badgeError: any) {
            // バッジ取得失敗時はフォールバック
            if (badgeError.response?.status === 401) {
              try {
                const newToken = await refreshTokenFunction();
                if (newToken) {
                  const retryResponse = await getRankBadge(newToken);
                  const badgeBlob = new Blob([retryResponse.data], { type: retryResponse.headers['content-type'] || 'image/png' });
                  const url = URL.createObjectURL(badgeBlob);
                  setBadgeUrl(url);
                  setBadgeError(false);
                }
              } catch (refreshError) {
                console.error('Failed to refresh token:', refreshError);
                setBadgeError(true);
              }
            } else {
              console.error('Failed to fetch badge:', badgeError);
              setBadgeError(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch data, using cached:', error);
      }
    };

    fetchData();
  }, [token, refreshTokenFunction, message?.points, message?.times]);

  const bgColorHandler = (rank: number | undefined) => {
    if (rank !== undefined) {
      if (rank > 6) return 'white';
      else return 'black';
    }
    return 'black';
  };

  return (
    <div>
      {/* QRコードは常に表示（messageがあれば） */}
      {message ? (
        // 通常表示
        <Box sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: { xs: 7, sm: 8 },
          paddingTop: { xs: 2, sm: 3 },
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '500px', 
            height: { xs: '350px', sm: '400px' }, 
            top: { xs: 56, sm: 64 }, 
            position: 'fixed', 
            zIndex: -1,
            left: 0,
            right: 0,
            margin: '0 auto',
          }}>
            {badgeUrl ? (
              <img src={badgeUrl} style={{ width: "100%", height: "100%", objectFit: 'contain' }} alt="Rank Badge" />
            ) : badgeError ? (
              <Box sx={{
                width: "100%",
                height: "100%",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#ffe6e6',
                color: '#cc0000',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                バッジ画像の読み込みに失敗しました
              </Box>
            ) : (
              <Box sx={{
                width: "100%",
                height: "100%",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                color: '#999',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                バッジ画像を読み込み中...
              </Box>
            )}
          </Box>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '500px',
            height: { xs: '350px', sm: '400px' }, 
            top: { xs: 56, sm: 64 }, 
            position: 'fixed', 
            zIndex: 0,
            left: 0,
            right: 0,
            margin: '0 auto',
          }}>
            <Box sx={{
              width: { xs: "calc(100% - 20px)", sm: "100%" },
              padding: { xs: '10px', sm: '0' },
              position: 'relative',
              top: '150px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}>
              <Typography 
                color={bgColorHandler(message?.rank)}
                sx={{
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                  fontWeight: 'bold',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                {message?.name} 様
              </Typography>
              <Paper
                sx={{
                  zIndex: 0,
                  width: { xs: '80%', sm: '80vw' },
                  maxWidth: "400px",
                  bgcolor: 'white',
                  opacity: 0.8,
                  marginTop: '40px',
                  boxSizing: 'border-box',
                }}>
                <Grid container direction={'row'}>
                  <Grid item xs={6} padding={1} paddingBottom={0}>
                    <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
                      <Typography variant='body1'>
                        もマイル
                      </Typography>
                      <Typography variant='body1'>
                        {message?.points}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} padding={1} paddingBottom={0}>
                    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                      <Typography variant='body1'>
                        来場回数
                      </Typography>
                      <Typography variant='body1'>
                        {message?.times}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} marginTop={0}>
                  <Typography variant='caption' color="textSecondary">
                    最終付与日: {lastIncrementDate ? new Date(lastIncrementDate).toLocaleDateString() : '取得中...'}
                  </Typography>
                </Box>
              </Paper>
              <Paper
                sx={{
                  zIndex: 0,
                  width: { xs: '80%', sm: '80vw' },
                  maxWidth: "400px",
                  height: "35vh",
                  bgcolor: 'white',
                  marginTop: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                }}>
                <QRCodeComponent text={message?.email} secretKey={seacret_key} />
              </Paper>
            </Box>
          </Box>
        </Box>
      ) : (
        // ユーザー情報がない場合のフォールバック
        <Box sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5'
        }}>
          <CircularProgress size={60} sx={{ marginBottom: 2 }} />
          <Typography variant="h6" color="textSecondary">
            読み込み中...
          </Typography>
        </Box>
      )}
    </div>
  );
};
export default IDComponent;